import React, { useState, useEffect, useCallback } from 'react';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartLine,
  faShoppingCart,
  faCalendar,
  faArrowUp,
  faArrowDown,
  faGlobe,
  faDesktop,
  faMobile,
  faSync,
  faDownload,
  faLayerGroup,
  faUserPlus,
  faEye,
  faDatabase,
  faComments,
  faDollarSign,
  faTable,
  faHdd
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark' ||
           document.body.classList.contains('dark-mode');
  });
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalGroups: 0,
    activeUsers: 0,
    newUsersToday: 0,
    totalMessages: 0,
    totalVentes: 0,
    usersByCountry: [],
    visitorsByCountry: [],
    userActivity: [],
    deviceStats: { desktop: 0, mobile: 0, tablet: 0 },
    topGroups: [],
    recentActivity: [],
    growthRate: 0,
    retentionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [dbOverview, setDbOverview] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [users, groups, usersByCountry, userActivity, devices, topGroups] = await Promise.all([
        api.get('/admin/stats/users'),
        api.get('/admin/stats/groups'),
        api.get(`/admin/analytics/users-by-country?days=${timeRange}`),
        api.get(`/admin/analytics/user-activity?days=${timeRange}`),
        api.get('/admin/analytics/device-stats'),
        api.get('/admin/analytics/top-groups')
      ]);

      // Try to get additional stats
      let messagesCount = 0;
      let ventesTotal = 0;
      try {
        const dataUsageRes = await api.get('/admin/groups/data-usage/all');
        if (dataUsageRes.data) {
          messagesCount = dataUsageRes.data.reduce((acc, g) => acc + (g.messages_count || 0), 0);
          ventesTotal = dataUsageRes.data.reduce((acc, g) => acc + parseFloat(g.ventes_total || 0), 0);
        }
      } catch (e) {
        console.log('Could not fetch data usage stats');
      }

      // Fetch database overview for the new analytics section
      try {
        const dbRes = await api.get('/admin/analytics/database-overview');
        if (dbRes.data?.success) {
          setDbOverview(dbRes.data);
        }
      } catch (e) {
        console.log('Could not fetch database overview');
      }

      // Fetch Accueil visitor breakdown by country (IP-based geolocation)
      let visitorsByCountry = [];
      try {
        const visitsRes = await api.get(`/admin/analytics/visits-by-country?days=${timeRange}`);
        visitorsByCountry = visitsRes.data || [];
      } catch (e) {
        console.log('Could not fetch visitor country stats');
      }

      const activityData = userActivity.data || [];
      const growthRate = activityData.length >= 2 
        ? ((activityData[activityData.length - 1]?.count || 0) - (activityData[0]?.count || 0)) 
        : 0;

      setAnalytics({
        totalUsers: users.data.total || 0,
        totalGroups: groups.data.total || 0,
        activeUsers: users.data.active || 0,
        newUsersToday: activityData[activityData.length - 1]?.count || 0,
        totalMessages: messagesCount,
        totalVentes: ventesTotal,
        usersByCountry: usersByCountry.data || [],
        visitorsByCountry,
        userActivity: activityData,
        deviceStats: devices.data || { desktop: 0, mobile: 0, tablet: 0 },
        topGroups: topGroups.data || [],
        growthRate,
        retentionRate: users.data.total > 0 ? Math.round((users.data.active / users.data.total) * 100) : 0
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Watch for theme changes
  useEffect(() => {
    const checkTheme = () => {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark' ||
                   document.body.classList.contains('dark-mode');
      setIsDark(dark);
    };

    // Check on mount
    checkTheme();

    // Watch for attribute changes on document element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-theme' || mutation.attributeName === 'class')) {
          checkTheme();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const exportData = () => {
    const exportObj = {
      exportDate: new Date().toISOString(),
      timeRange: `${timeRange} jours`,
      ...analytics
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-export-${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart theme colors
  const chartColors = {
    primary: '#6366f1',
    secondary: '#10b981',
    accent: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    pink: '#ec4899',
    cyan: '#06b6d4'
  };

  const userActivityData = {
    labels: analytics.userActivity.map(s => {
      const date = new Date(s.date);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    }),
    datasets: [
      {
        label: 'Nouveaux Utilisateurs',
        data: analytics.userActivity.map(s => s.count),
        borderColor: chartColors.primary,
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: chartColors.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const countryData = {
    labels: analytics.usersByCountry.slice(0, 6).map(c => c.country || 'Non spécifié'),
    datasets: [
      {
        label: 'Utilisateurs',
        data: analytics.usersByCountry.slice(0, 6).map(c => c.count),
        backgroundColor: [
          chartColors.primary,
          chartColors.secondary,
          chartColors.accent,
          chartColors.danger,
          chartColors.purple,
          chartColors.pink
        ],
        borderRadius: 8,
        borderSkipped: false
      }
    ]
  };

  const visitorCountryData = {
    labels: analytics.visitorsByCountry.slice(0, 6).map(c => c.country || 'Inconnu'),
    datasets: [
      {
        label: 'Visiteurs Accueil',
        data: analytics.visitorsByCountry.slice(0, 6).map(c => c.count),
        backgroundColor: [
          chartColors.cyan,
          chartColors.primary,
          chartColors.secondary,
          chartColors.accent,
          chartColors.purple,
          chartColors.pink
        ],
        borderRadius: 8,
        borderSkipped: false
      }
    ]
  };

  const deviceData = {
    labels: ['Desktop', 'Mobile', 'Tablette'],
    datasets: [
      {
        data: [
          analytics.deviceStats.desktop,
          analytics.deviceStats.mobile,
          analytics.deviceStats.tablet
        ],
        backgroundColor: [chartColors.primary, chartColors.secondary, chartColors.accent],
        borderWidth: 0,
        hoverOffset: 10
      }
    ]
  };

  // Dark mode chart colors
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: { size: 12 },
          color: textColor
        }
      }
    },
    scales: {
      x: {
        grid: { display: false, color: gridColor },
        ticks: { font: { size: 11 }, color: textColor }
      },
      y: {
        grid: { color: gridColor },
        ticks: { font: { size: 11 }, color: textColor }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: { size: 12 },
          color: textColor
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des analytiques...</p>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h1>Analytiques Avancées</h1>
          <p>Analyse détaillée de l'activité de la plateforme</p>
        </div>
        <div className="header-actions">
          <div className="time-range-selector">
            <button 
              className={timeRange === '7' ? 'active' : ''}
              onClick={() => setTimeRange('7')}
            >
              7 jours
            </button>
            <button 
              className={timeRange === '30' ? 'active' : ''}
              onClick={() => setTimeRange('30')}
            >
              30 jours
            </button>
            <button 
              className={timeRange === '90' ? 'active' : ''}
              onClick={() => setTimeRange('90')}
            >
              90 jours
            </button>
          </div>
          <button 
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Actualiser"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button className="export-btn" onClick={exportData}>
            <FontAwesomeIcon icon={faDownload} />
            Exporter
          </button>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="quick-stats-row">
        <div className="quick-stat-card">
          <div className="quick-stat-icon blue">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="quick-stat-content">
            <h4>Utilisateurs Total</h4>
            <span className="stat-value">{analytics.totalUsers.toLocaleString()}</span>
            <span className={`stat-change ${analytics.growthRate >= 0 ? 'positive' : 'negative'}`}>
              <FontAwesomeIcon icon={analytics.growthRate >= 0 ? faArrowUp : faArrowDown} />
              {Math.abs(analytics.growthRate)} cette période
            </span>
          </div>
        </div>

        <div className="quick-stat-card">
          <div className="quick-stat-icon green">
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>
          <div className="quick-stat-content">
            <h4>Groupes</h4>
            <span className="stat-value">{analytics.totalGroups.toLocaleString()}</span>
            <span className="stat-change positive">
              <FontAwesomeIcon icon={faArrowUp} /> Actifs
            </span>
          </div>
        </div>

        <div className="quick-stat-card">
          <div className="quick-stat-icon purple">
            <FontAwesomeIcon icon={faComments} />
          </div>
          <div className="quick-stat-content">
            <h4>Messages</h4>
            <span className="stat-value">{analytics.totalMessages.toLocaleString()}</span>
            <span className="stat-change positive">Total échangés</span>
          </div>
        </div>

        <div className="quick-stat-card">
          <div className="quick-stat-icon orange">
            <FontAwesomeIcon icon={faDollarSign} />
          </div>
          <div className="quick-stat-content">
            <h4>Ventes</h4>
            <span className="stat-value">{analytics.totalVentes.toLocaleString()} F</span>
            <span className="stat-change positive">Montant total</span>
          </div>
        </div>
      </div>

      {/* Main Metric Cards */}
      <div className="analytics-grid">
        <div className="metric-card blue">
          <div className="metric-icon">
            <FontAwesomeIcon icon={faUserPlus} />
          </div>
          <div className="metric-content">
            <h3>Nouveaux Aujourd'hui</h3>
            <p className="metric-value">{analytics.newUsersToday}</p>
            <span className="metric-change positive">
              <FontAwesomeIcon icon={faArrowUp} /> Dernière inscription
            </span>
          </div>
        </div>

        <div className="metric-card green">
          <div className="metric-icon">
            <FontAwesomeIcon icon={faEye} />
          </div>
          <div className="metric-content">
            <h3>Utilisateurs Actifs</h3>
            <p className="metric-value">{analytics.activeUsers}</p>
            <span className="metric-label">Derniers 30 jours</span>
          </div>
        </div>

        <div className="metric-card purple">
          <div className="metric-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="metric-content">
            <h3>Taux de Rétention</h3>
            <p className="metric-value">{analytics.retentionRate}%</p>
            <span className="metric-label">Utilisateurs actifs / total</span>
          </div>
        </div>
      </div>

      <div className="charts-section">
        <div className="chart-box large">
          <div className="chart-box-header">
            <div>
              <h3>Croissance des Utilisateurs</h3>
              <p className="chart-subtitle">Nouveaux utilisateurs sur {timeRange} jours</p>
            </div>
          </div>
          <div className="chart-container">
            <Line data={userActivityData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-box">
          <div className="chart-box-header">
            <h3>Utilisateurs par Pays</h3>
          </div>
          <div className="chart-container">
            <Bar data={countryData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-box">
          <div className="chart-box-header">
            <h3>Visiteurs Accueil par Pays</h3>
            <p className="chart-subtitle">Localisation IP des visiteurs de la page d'accueil</p>
          </div>
          <div className="chart-container">
            {analytics.visitorsByCountry.length > 0 ? (
              <Bar data={visitorCountryData} options={chartOptions} />
            ) : (
              <p className="no-data">Aucune visite enregistrée</p>
            )}
          </div>
        </div>

        <div className="chart-box">
          <div className="chart-box-header">
            <h3>Appareils Utilisés</h3>
          </div>
          <div className="chart-container">
            <Doughnut data={deviceData} options={pieOptions} />
          </div>
        </div>
      </div>

      {/* Performance Section */}
      <div className="performance-section">
        <h3>Indicateurs de Performance</h3>
        <div className="performance-grid">
          <div className="performance-item">
            <div className="performance-circle">
              <svg width="80" height="80">
                <circle className="bg" cx="40" cy="40" r="34" />
                <circle 
                  className="progress" 
                  cx="40" cy="40" r="34"
                  strokeDasharray={`${analytics.retentionRate * 2.13} 213`}
                  style={{ stroke: chartColors.primary }}
                />
              </svg>
              <span className="value">{analytics.retentionRate}%</span>
            </div>
            <h4>Rétention</h4>
            <p>Taux d'utilisateurs actifs</p>
          </div>

          <div className="performance-item">
            <div className="performance-circle">
              <svg width="80" height="80">
                <circle className="bg" cx="40" cy="40" r="34" />
                <circle 
                  className="progress" 
                  cx="40" cy="40" r="34"
                  strokeDasharray={`${Math.min((analytics.totalGroups / Math.max(analytics.totalUsers, 1)) * 100 * 2.13, 213)} 213`}
                  style={{ stroke: chartColors.secondary }}
                />
              </svg>
              <span className="value">{Math.round((analytics.totalGroups / Math.max(analytics.totalUsers, 1)) * 100)}%</span>
            </div>
            <h4>Engagement</h4>
            <p>Ratio groupes/utilisateurs</p>
          </div>

          <div className="performance-item">
            <div className="performance-circle">
              <svg width="80" height="80">
                <circle className="bg" cx="40" cy="40" r="34" />
                <circle 
                  className="progress" 
                  cx="40" cy="40" r="34"
                  strokeDasharray={`${Math.min((analytics.deviceStats.mobile / Math.max(analytics.deviceStats.desktop + analytics.deviceStats.mobile + analytics.deviceStats.tablet, 1)) * 100 * 2.13, 213)} 213`}
                  style={{ stroke: chartColors.accent }}
                />
              </svg>
              <span className="value">
                {Math.round((analytics.deviceStats.mobile / Math.max(analytics.deviceStats.desktop + analytics.deviceStats.mobile + analytics.deviceStats.tablet, 1)) * 100)}%
              </span>
            </div>
            <h4>Mobile</h4>
            <p>Utilisateurs sur mobile</p>
          </div>

          <div className="performance-item">
            <div className="performance-circle">
              <svg width="80" height="80">
                <circle className="bg" cx="40" cy="40" r="34" />
                <circle 
                  className="progress" 
                  cx="40" cy="40" r="34"
                  strokeDasharray={`${Math.min(analytics.usersByCountry.length * 10 * 2.13, 213)} 213`}
                  style={{ stroke: chartColors.purple }}
                />
              </svg>
              <span className="value">{analytics.usersByCountry.length}</span>
            </div>
            <h4>Pays</h4>
            <p>Présence internationale</p>
          </div>
        </div>
      </div>

      <div className="top-products-section">
        <h3>Groupes les Plus Actifs</h3>
        <div className="products-grid">
          {analytics.topGroups.map((group, index) => (
            <div key={index} className="product-card">
              <div className={`product-rank ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}>
                #{index + 1}
              </div>
              <div className="product-info">
                <h4>{group.nom || group.nom_groupe || 'Groupe'}</h4>
                <p className="product-sales">{group.member_count || 0} membres</p>
                <p className="product-revenue">
                  Créé: {new Date(group.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          ))}
          {analytics.topGroups.length === 0 && (
            <p className="no-data">Aucun groupe disponible</p>
          )}
        </div>
      </div>

      {/* Database Analytics Section */}
      {dbOverview && (
        <div className="database-analytics-section">
          <div className="section-header-row">
            <h3>
              <FontAwesomeIcon icon={faDatabase} style={{ marginRight: '0.5rem', color: '#6366f1' }} />
              Analytiques Base de Données
            </h3>
            <span className="db-size-badge">
              <FontAwesomeIcon icon={faHdd} style={{ marginRight: '0.35rem' }} />
              {dbOverview.summary.databaseSize}
            </span>
          </div>

          <div className="db-summary-cards">
            <div className="db-summary-card">
              <div className="db-summary-icon blue">
                <FontAwesomeIcon icon={faTable} />
              </div>
              <div className="db-summary-info">
                <h4>Tables Totales</h4>
                <span className="db-summary-value">{dbOverview.summary.totalTables}</span>
                <span className="db-summary-sub">{dbOverview.summary.tablesWithData} avec données</span>
              </div>
            </div>
            <div className="db-summary-card">
              <div className="db-summary-icon green">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div className="db-summary-info">
                <h4>Enregistrements</h4>
                <span className="db-summary-value">{(dbOverview.summary.totalRecords || 0).toLocaleString('fr-FR')}</span>
                <span className="db-summary-sub">Toutes tables confondues</span>
              </div>
            </div>
            <div className="db-summary-card">
              <div className="db-summary-icon purple">
                <FontAwesomeIcon icon={faHdd} />
              </div>
              <div className="db-summary-info">
                <h4>Taille Base</h4>
                <span className="db-summary-value">{dbOverview.summary.databaseSize}</span>
                <span className="db-summary-sub">Stockage total</span>
              </div>
            </div>
          </div>

          <div className="db-tables-grid">
            {dbOverview.tables
              .filter(t => t.count > 0)
              .map((table, index) => {
                const maxCount = Math.max(...dbOverview.tables.map(t => t.count), 1);
                const pct = ((table.count / maxCount) * 100).toFixed(0);
                return (
                  <div key={table.name} className="db-table-row">
                    <div className="db-table-info">
                      <span className="db-table-name">{table.label}</span>
                      <span className="db-table-tech">{table.name}</span>
                    </div>
                    <div className="db-table-bar-wrap">
                      <div 
                        className="db-table-bar" 
                        style={{ 
                          width: `${Math.max(pct, 2)}%`,
                          backgroundColor: ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'][index % 8]
                        }} 
                      />
                    </div>
                    <div className="db-table-stats">
                      <span className="db-table-count">{table.count.toLocaleString('fr-FR')}</span>
                      <span className="db-table-size">{table.size}</span>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Empty tables summary */}
          {(() => {
            const emptyTables = dbOverview.tables.filter(t => t.count === 0);
            return emptyTables.length > 0 ? (
              <div className="db-empty-tables">
                <span className="db-empty-count">{emptyTables.length} tables vides :</span>
                <span className="db-empty-names">{emptyTables.map(t => t.label).join(', ')}</span>
              </div>
            ) : null;
          })()}
        </div>
      )}

      <div className="insights-section">
        <h3>Insights & Recommandations</h3>
        <div className="insights-grid">
          <div className="insight-card purple">
            <FontAwesomeIcon icon={faGlobe} className="insight-icon" />
            <h4>Expansion Géographique</h4>
            <p>
              {analytics.usersByCountry.length > 0
                ? `Présence dans ${analytics.usersByCountry.length} pays différents`
                : 'Commencez à cibler différents marchés'}
            </p>
            <div className="insight-value">{analytics.usersByCountry.length} pays</div>
          </div>
          <div className="insight-card blue">
            <FontAwesomeIcon icon={faMobile} className="insight-icon" />
            <h4>Optimisation Mobile</h4>
            <p>
              {analytics.deviceStats.mobile > analytics.deviceStats.desktop
                ? 'La majorité des utilisateurs sont sur mobile'
                : 'Desktop domine, considérez l\'optimisation mobile'}
            </p>
            <div className="insight-value">
              {Math.round((analytics.deviceStats.mobile / Math.max(analytics.deviceStats.desktop + analytics.deviceStats.mobile, 1)) * 100)}% mobile
            </div>
          </div>
          <div className="insight-card green">
            <FontAwesomeIcon icon={faCalendar} className="insight-icon" />
            <h4>Croissance</h4>
            <p>
              Analysez les tendances sur {timeRange} jours pour identifier les opportunités
            </p>
            <div className="insight-value">
              {analytics.growthRate >= 0 ? '+' : ''}{analytics.growthRate} utilisateurs
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
